import React, { useState, useEffect, useReducer } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
// import config
import theme from '../config/theme';
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import isEmpty from '../lib/isEmpty';
import toastAlert from '../lib/toastAlert';
// import validation
import { changePasswordValid } from '../validation/user.validation';
// import services
import { changePassword } from '../services/user.service';
// import components
import Input from '../custom/Input';
import ErrorSpan from '../custom/ErrorSpan';
import EyeIcon from '../custom/EyeIcon';
import ButtonLoader from '../custom/ButtonLoader';

const initialFormValue = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
};

const ChangePassword = () => {
    // state
    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loader, setLoader] = useState(false);
    const { currentPassword, newPassword, confirmPassword } = formValue;

    // password requirements
    const passwordRequirements = [
        { id: 1, text: 'At least 8 characters', regex: /.{8,}/ },
        { id: 2, text: 'One uppercase letter', regex: /[A-Z]/ },
        { id: 3, text: 'One lowercase letter', regex: /[a-z]/ },
        { id: 4, text: 'One number', regex: /[0-9]/ },
        { id: 5, text: 'One special character', regex: /[!@#$%^&*(),.?":{}|<>]/ }
    ];

    const handleChange = e => textChange(e, setFormValue);
    const handleBlur = e => onBlur(e, setTouched);

    const getPasswordStrength = (password) => {
        if (!password) return 0;
        let strength = 0;
        passwordRequirements.forEach(req => {
            if (req.regex.test(password)) strength++;
        });
        return (strength / passwordRequirements.length) * 100;
    };

    const getStrengthColor = (strength) => {
        if (strength < 40) return 'bg-red-500';
        if (strength < 70) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        const { status, message, errors } = await changePassword(formValue);

        if (status === true) {
            toastAlert('success', message, 'changePassword');
            setFormValue({ type: 'SET', value: initialFormValue });
            setErrors({});
            setTouched({});
        } else if (errors) {
            setErrors(errors);
        } else if (message) {
            toastAlert('error', message, 'changePassword');
        }
        setLoader(false);
    };

    useEffect(() => {
        const { status, errors } = changePasswordValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    const passwordStrength = getPasswordStrength(newPassword);

    // console.log(formValue, 'formValueformValueformValue')
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            <div className="max-w-md mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                            Change Password
                        </h1>
                        <p className="text-gray-600">
                            Secure your account with a new password
                        </p>
                    </div>
                </div>

                {/* Password Form */}
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                Current Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    type={showCurrentPassword ? "text" : "password"}
                                    name="currentPassword"
                                    value={currentPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="••••••••"
                                    mode="light"
                                    errors={errors}
                                    touched={touched}
                                />
                                <EyeIcon showPassword={showCurrentPassword} setShowPassword={setShowCurrentPassword} />
                            </div>
                            <ErrorSpan errors={errors} touched={touched} fieldName={'currentPassword'} />
                        </div>

                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    type={showNewPassword ? "text" : "password"}
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="••••••••"
                                    mode="light"
                                    errors={errors}
                                    touched={touched}
                                />
                                <EyeIcon showPassword={showNewPassword} setShowPassword={setShowNewPassword} />
                            </div>

                            {/* {
                                newPassword && (
                                    <div className="mt-3">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs text-gray-600">Password strength</span>
                                            <span className="text-xs font-medium text-gray-700">
                                                {passwordStrength < 40 ? 'Weak' : passwordStrength < 70 ? 'Good' : 'Strong'}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                                                style={{ width: `${passwordStrength}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )
                            } */}

                            {/* <div className="mt-3 space-y-2">
                                <p className="text-xs text-gray-600 font-medium">Password must contain:</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {passwordRequirements.map(req => (
                                        <div key={req.id} className="flex items-center gap-2">
                                            {req.regex.test(newPassword) ? (
                                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                            ) : (
                                                <div className="w-4 h-4 border-2 border-gray-300 rounded-full flex-shrink-0"></div>
                                            )}
                                            <span className={`text-xs ${req.regex.test(newPassword)
                                                ? 'text-green-600'
                                                : 'text-gray-500'
                                                }`}>
                                                {req.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div> */}

                            <ErrorSpan errors={errors} touched={touched} fieldName={'newPassword'} />
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <Input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="••••••••"
                                    mode="light"
                                    errors={errors}
                                    touched={touched}
                                />
                                <EyeIcon showPassword={showConfirmPassword} setShowPassword={setShowConfirmPassword} />
                            </div>
                            <ErrorSpan errors={errors} touched={touched} fieldName={'confirmPassword'} />
                        </div>

                        <ButtonLoader
                            type="button"
                            onClick={handleSubmit}
                            loader={loader}
                            disabled={!isEmpty(errors)}
                            btnName='Update Password'
                        />
                    </form>
                </div>

                {/* Security Tips */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Security Tips
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Use a unique password that you don't use elsewhere</li>
                        <li>• Avoid using personal information in your password</li>
                        <li>• Consider using a password manager</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;